import { isAxiosError } from 'axios';
import { inferenceHttp } from '@/lib/api/inference-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { AgentDto as ApiAgentDto, AgentsService } from '@inference.engine/api-client/web';
import type { Agent } from '../model/agent.types';
import { logger } from '@/lib/utils/logger';
import type { AgentUsageDto } from './agents.api.types';

/**
 * Maps API AgentDto to UI Agent type
 */
function mapApiAgentToAgent(apiAgent: ApiAgentDto): Agent {
  return {
    id: apiAgent.id,
    title: apiAgent.name, // Map name to title for UI compatibility
    description: apiAgent.description,
    avatar: apiAgent.avatar,
    tag: apiAgent.tag,
    capabilities: apiAgent.capabilities,
    greeting: apiAgent.greeting,
    placeholderPrompt: apiAgent.placeholderPrompt,
    enabledModels: apiAgent.enabledModels,
    configurationStatus: apiAgent.configurationStatus,
    isDeleted: apiAgent.isDeleted,
    ...(apiAgent.suggestions !== undefined ? { suggestions: apiAgent.suggestions } : {}),
    ...(apiAgent.visibility ? { visibility: apiAgent.visibility } : {}),
  };
}

type GetAgentsOptions = NonNullable<Parameters<typeof AgentsService.getAllAgents>[0]>;
type GetAgentsQueryParams = NonNullable<GetAgentsOptions['query']>;

export const getAgents = async (params?: GetAgentsQueryParams): Promise<Agent[]> => {
  try {
    const response = await AgentsService.getAllAgents({
      client: inferenceHttp,
      query: params,
    });
    return (response.data ?? []).map(mapApiAgentToAgent);
  } catch (error) {
    logger.error('Failed to fetch agents:', error);
    return [];
  }
};

/** Statuses meaning the card is gone or not the caller's, as opposed to a failure worth retrying. */
const AGENT_UNAVAILABLE_STATUSES = new Set([403, 404]);

/**
 * The agent, or `null` when its card cannot be loaded: gone, or not the caller's to use. Any other
 * failure throws, so a query does not cache an outage as a missing agent.
 */
export const getAgentById = async (agentId?: string): Promise<Agent | null> => {
  if (!agentId) return null;

  try {
    const response = await AgentsService.getAgentById({
      client: inferenceHttp,
      path: { id: agentId },
      // The caller shows an agent that cannot be loaded; any other failure still toasts.
      headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: [...AGENT_UNAVAILABLE_STATUSES].join(',') },
      throwOnError: true,
    });
    return mapApiAgentToAgent(response.data);
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    if (status !== undefined && AGENT_UNAVAILABLE_STATUSES.has(status)) return null;
    logger.error('Failed to get agent by id:', error);
    throw error;
  }
};

/**
 * Returns the most used agents for the current user.
 *
 * Agents are ranked by a score calculated from messageCount + contextCount.
 * If fewer agents are found than the requested limit, the remaining slots
 * are filled with other allowed agents with a score of 0.
 *
 * @param limit - Maximum number of agents to return (default: 4)
 * @returns Array of agent usage data with agentId and score
 */
export const getMostUsedAgents = async (limit = 4): Promise<AgentUsageDto[]> => {
  try {
    const response = await AgentsService.getMostUsedAgents({
      client: inferenceHttp,
      query: { limit },
    });
    return response.data ?? [];
  } catch (error) {
    logger.error('Failed to fetch most used agents', error);
    return [];
  }
};
